import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface Visit {
    id: string
    facility_id: string | null
    visit_type: 'operations' | 'accreditation' | 'acquisition' | 'service_line' | 'daily_self_assessment' | 'unknown'
    facility_name: string
    address: string
    city?: string | null
    state?: string | null
    zip_code?: string | null
    administrator_name?: string | null
    don_name?: string | null
    specialties?: string[]
    visit_scope?: string
    status: 'scheduled' | 'in_progress' | 'in_review' | 'released' | 'archived'
    assigned_surveyor_id?: string | null
    surveyor_name: string
    visit_date: string
}

export interface ChecklistItem {
    template_id: string
    question_text: string
    standard_id: string
    standard_title: string
}

interface BackendVisitRow {
    visit_id: string
    status: string
    visit_type?: string | null
    survey_date?: string | null
    created_at?: string | null
    assigned_surveyor_id?: string | null
    facility?: {
        facility_id?: string | null
        facility_name?: string | null
        address?: string | null
        city?: string | null
        state?: string | null
        zip_code?: string | null
        contact_name?: string | null
        don_name?: string | null
        director_of_nursing?: string | null
        specialties?: unknown
    } | null
}

interface BackendChecklistItem {
    template_id: string
    question_text: string
    standards?: Array<{
        standard_id?: string
        title?: string
    }>
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function isBackendVisitStatus(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0
}

function normalizeVisitStatus(value: string): Visit['status'] {
    const status = value.trim().toLowerCase()
    if (status === 'released' || status === 'archived' || status === 'in_review') return status
    if (status === 'complete' || status === 'completed' || status === 'done') return 'released'
    if (status === 'in_progress' || status === 'active') return 'in_progress'
    return 'scheduled'
}

function asBackendVisitRow(value: unknown): BackendVisitRow | null {
    if (!isRecord(value)) return null
    if (typeof value.visit_id !== 'string' || !value.visit_id.trim()) return null
    if (!isBackendVisitStatus(value.status)) return null

    const facility = isRecord(value.facility)
        ? {
              facility_id: typeof value.facility.facility_id === 'string' ? value.facility.facility_id : null,
              facility_name: typeof value.facility.facility_name === 'string' ? value.facility.facility_name : null,
              address: typeof value.facility.address === 'string' ? value.facility.address : null,
              city: typeof value.facility.city === 'string' ? value.facility.city : null,
              state: typeof value.facility.state === 'string' ? value.facility.state : null,
              zip_code: typeof value.facility.zip_code === 'string' ? value.facility.zip_code : null,
              contact_name: typeof value.facility.contact_name === 'string' ? value.facility.contact_name : null,
              don_name: typeof value.facility.don_name === 'string' ? value.facility.don_name : null,
              director_of_nursing: typeof value.facility.director_of_nursing === 'string' ? value.facility.director_of_nursing : null,
              specialties: value.facility.specialties,
          }
        : null

    return {
        visit_id: value.visit_id,
        status: value.status,
        visit_type: typeof value.visit_type === 'string' ? value.visit_type : null,
        survey_date: typeof value.survey_date === 'string' ? value.survey_date : null,
        created_at: typeof value.created_at === 'string' ? value.created_at : null,
        assigned_surveyor_id: typeof value.assigned_surveyor_id === 'string' ? value.assigned_surveyor_id : null,
        facility,
    }
}

function asBackendChecklistItem(value: unknown): BackendChecklistItem | null {
    if (!isRecord(value)) return null
    if (typeof value.template_id !== 'string' || !value.template_id.trim()) return null
    if (typeof value.question_text !== 'string' || !value.question_text.trim()) return null

    const standards = Array.isArray(value.standards)
        ? value.standards
              .filter((entry) => isRecord(entry))
              .map((entry) => ({
                  standard_id: typeof entry.standard_id === 'string' ? entry.standard_id : undefined,
                  title: typeof entry.title === 'string' ? entry.title : undefined,
              }))
        : undefined

    return {
        template_id: value.template_id,
        question_text: value.question_text,
        standards,
    }
}

function normalizeVisit(row: BackendVisitRow): Visit {
    const cityStateZip = [row.facility?.city, row.facility?.state, row.facility?.zip_code].filter(Boolean).join(', ')
    const address = [row.facility?.address, cityStateZip].filter(Boolean).join(' · ') || row.facility?.state || 'Unknown Location'
    const specialties = Array.isArray(row.facility?.specialties)
        ? row.facility.specialties.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
        : []

    return {
        id: row.visit_id,
        facility_id: row.facility?.facility_id || null,
        visit_type:
            row.visit_type === 'operations' ||
            row.visit_type === 'accreditation' ||
            row.visit_type === 'acquisition' ||
            row.visit_type === 'service_line' ||
            row.visit_type === 'daily_self_assessment'
                ? row.visit_type
                : 'unknown',
        facility_name: row.facility?.facility_name || 'Unknown Facility',
        address,
        city: row.facility?.city || null,
        state: row.facility?.state || null,
        zip_code: row.facility?.zip_code || null,
        administrator_name: row.facility?.contact_name || null,
        don_name: row.facility?.don_name || row.facility?.director_of_nursing || null,
        specialties,
        visit_scope: formatVisitScope(row.visit_type),
        status: normalizeVisitStatus(row.status),
        assigned_surveyor_id: row.assigned_surveyor_id || null,
        surveyor_name: row.assigned_surveyor_id ? 'Assigned Surveyor' : 'Unassigned',
        visit_date: row.survey_date || row.created_at || new Date().toISOString(),
    }
}

function formatVisitScope(value: string | null | undefined): string {
    if (value === 'accreditation') return 'Accreditation survey scope'
    if (value === 'operations') return 'Operations survey scope'
    if (value === 'acquisition') return 'Acquisition assessment scope'
    if (value === 'service_line') return 'Service line assessment scope'
    if (value === 'daily_self_assessment') return 'Daily assessment scope'
    return 'Survey scope'
}

/**
 * Visit Service [FE.1.1]
 * Uses backend Edge Function route paths under the `visits` function.
 */
export async function getAssignments(): Promise<Visit[]> {
    const { data, error } = await invokeFunction<{ visits: unknown }>('visits', {
        method: 'GET',
        path: '',
    })

    if (error) {
        throw toApiRequestError(error, 'Failed to load visits')
    }

    if (!data || !Array.isArray(data.visits)) {
        throw new Error('Malformed visits response from server')
    }

    const normalized: BackendVisitRow[] = []
    for (const row of data.visits) {
        const parsed = asBackendVisitRow(row)
        if (!parsed) {
            throw new Error('Malformed visit entry in server response')
        }
        normalized.push(parsed)
    }

    return normalized.map(normalizeVisit)
}

export async function getVisitDetails(id: string): Promise<Visit> {
    const { data, error } = await invokeFunction<unknown>('visits', {
        method: 'GET',
        path: `/${id}`,
    })

    if (error || !data) {
        throw toApiRequestError(error, 'Visit not found')
    }

    const normalized = asBackendVisitRow(data)
    if (!normalized) {
        throw new Error('Malformed visit response from server')
    }

    return normalizeVisit(normalized)
}

export async function getVisitChecklist(visitId: string): Promise<ChecklistItem[]> {
    const { data, error } = await invokeFunction<{ checklist_items: unknown }>('visits', {
        method: 'GET',
        path: `/${visitId}/checklist`,
    })

    if (error) {
        throw toApiRequestError(error, 'Failed to load checklist')
    }

    if (!data || !Array.isArray(data.checklist_items)) {
        throw new Error('Malformed checklist response from server')
    }

    const normalized: BackendChecklistItem[] = []
    for (const item of data.checklist_items) {
        const parsed = asBackendChecklistItem(item)
        if (!parsed) {
            throw new Error('Malformed checklist item in server response')
        }
        normalized.push(parsed)
    }

    return normalized.map((item) => ({
        template_id: item.template_id,
        question_text: item.question_text,
        standard_id: item.standards?.[0]?.standard_id || item.template_id,
        standard_title: item.standards?.[0]?.title || 'Checklist standard',
    }))
}

export async function createAssessmentVisit(input: {
    facility_id: string
    visit_type: 'acquisition' | 'service_line'
    assigned_surveyor_id: string
    survey_date?: string
}): Promise<Visit> {
    const { data, error } = await invokeFunction<unknown>('visits', {
        method: 'POST',
        path: '',
        body: input,
    })

    if (error || !data) {
        throw toApiRequestError(error, 'Failed to create assessment visit')
    }

    const normalized = asBackendVisitRow(data)
    if (!normalized) {
        throw new Error('Malformed visit create response from server')
    }

    return normalizeVisit(normalized)
}

export async function createSiteVisit(input: {
    facility_id: string
    visit_type: 'operations' | 'accreditation'
    assigned_surveyor_id: string
    survey_date?: string
}): Promise<Visit> {
    const { data, error } = await invokeFunction<unknown>('visits', {
        method: 'POST',
        path: '',
        body: input,
    })

    if (error || !data) {
        throw toApiRequestError(error, 'Failed to create site visit')
    }

    const normalized = asBackendVisitRow(data)
    if (!normalized) {
        throw new Error('Malformed visit create response from server')
    }

    return normalizeVisit(normalized)
}

/** Lifecycle write targets accepted by POST /visits/:id/transition (#1854). */
export type VisitTransitionTarget = 'draft' | 'in_review' | 'released' | 'archived'

export interface UpdateVisitInput {
    assigned_surveyor_id?: string | null
    survey_date?: string
    visit_type?: Visit['visit_type']
}

/**
 * PATCH /visits/:id — reschedule / reassign / visit_type (admin|editor|superadmin).
 * Backend rejects released/archived visits. Wire for #1854 lifecycle UI.
 */
export async function updateVisit(visitId: string, input: UpdateVisitInput): Promise<Visit> {
    const body: Record<string, string | null> = {}
    if (input.assigned_surveyor_id !== undefined) {
        body.assigned_surveyor_id = input.assigned_surveyor_id
    }
    if (input.survey_date !== undefined) {
        body.survey_date = input.survey_date
    }
    if (input.visit_type !== undefined) {
        body.visit_type = input.visit_type
    }

    if (Object.keys(body).length === 0) {
        throw new Error('No valid fields to update')
    }

    const { data, error } = await invokeFunction<unknown>('visits', {
        method: 'PATCH',
        path: `/${visitId}`,
        body,
    })

    if (error || !data) {
        throw toApiRequestError(error, 'Failed to update visit')
    }

    const normalized = asBackendVisitRow(data)
    if (!normalized) {
        throw new Error('Malformed visit update response from server')
    }

    return normalizeVisit(normalized)
}

/**
 * POST /visits/:id/transition — status lifecycle (admin|editor|superadmin).
 * Release requires admin/superadmin + in_review + pin gate. Wire for #1854.
 */
export async function transitionVisit(
    visitId: string,
    targetStatus: VisitTransitionTarget,
): Promise<Visit> {
    const { data, error } = await invokeFunction<unknown>('visits', {
        method: 'POST',
        path: `/${visitId}/transition`,
        body: { target_status: targetStatus },
    })

    if (error || !data) {
        throw toApiRequestError(error, 'Failed to transition visit')
    }

    const normalized = asBackendVisitRow(data)
    if (!normalized) {
        throw new Error('Malformed visit transition response from server')
    }

    return normalizeVisit(normalized)
}
